/**
 * Generation Tools
 *
 * AI agent tools for generating content:
 * generate_image (Gemini), analyze_image (Gemini vision), build_spatial (procedural scene builder).
 */

import { dispatchCatalogImport } from '../../catalog-import';
import type { ToolDefinition } from './types';
import { buildRestoredGeometryContext, contextObjectsSchema } from '../geometry-context';
import { readTransformArgs } from 'trace-viewer/editor';
import { editModeOnly } from './authoring-ops';
import type { ToolContext } from '../agent-helpers';
import { applyTransform, groundAlign, resolveObject } from '../agent-helpers';
import { importBlobIntoSceneFolder, registerFolderFileName } from '../../cms/import-into-folder';
import { ScreenshotTools } from '@babylonjs/core/Misc/screenshotTools';
import { executeSpatialCode, type SpatialSceneSnapshot } from '../spatial-sandbox';
import { Model, Image, Video } from 'trace-viewer/behaviors';
import { boundsInParent, isEngineRoot, topLevelParent } from 'trace-viewer/editor';
import { materializeSpatialBuild } from '../spatial-materializer';
import { getImageBlob } from '../../inference/shared';
import { callCapability, callCapabilityFile, getGatewayRoute } from '../gateway';

/** What the tool timeline shows in place of a model id. */
function imageModelLabel(role: 'image' | 'vision'): () => string {
    return () => getGatewayRoute(role)?.label ?? 'Trace AI';
}

// ── generate_image ─────────────────────────────────────────

/**
 * Generate an image through the gateway and return the raw bytes, or an error string.
 * Shared by generate_image (in-loop), resolvePendingImages (post-loop) and generate_splat from a prompt.
 * The provider behind `image` is a gateway routing decision, switchable server-side without an editor release.
 */
export async function generateImageBytes(
    ctx: Pick<ToolContext, 'signal'>,
    prompt: string,
): Promise<{ bytes: Uint8Array; blob: Blob; extension: string } | { error: string }> {
    if (!prompt?.trim()) return { error: 'A text prompt is required to generate an image.' };
    try {
        // The answer is the picture itself, typed.
        const blob = await callCapabilityFile('images/generate', { prompt }, ctx.signal);
        if (!/^image\/(png|jpeg|webp)$/.test(blob.type)) return { error: `The gateway answered no picture (${blob.type || 'no type'}).` };
        return { bytes: new Uint8Array(await blob.arrayBuffer()), blob, extension: blob.type === 'image/jpeg' ? 'jpg' : blob.type.slice('image/'.length) };
    } catch (err) {
        return { error: err instanceof Error ? err.message : String(err) };
    }
}

/**
 * Persist generated image bytes as a scene-loadable asset and return its src.
 * Headless (node) also writes public/generated/ so the render harness can
 * serve it; in the browser the ResourceManager blob registration suffices.
 */
async function persistGeneratedImage(
    ctx: ToolContext,
    bytes: Uint8Array,
    blob: Blob,
    extension: string,
): Promise<string> {
    let hash: string;
    if (typeof process !== 'undefined' && process.versions?.node) {
        const { createHash } = await import('node:crypto');
        hash = createHash('sha256').update(bytes).digest('hex').slice(0, 16);
        const { writeFileSync, mkdirSync } = await import('node:fs');
        mkdirSync('public/generated', { recursive: true });
        writeFileSync(`public/generated/${hash}.${extension}`, bytes);
    } else {
        const digest = await crypto.subtle.digest('SHA-256', bytes.slice().buffer);
        hash = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
    }
    const relSrc = `/generated/${hash}.${extension}`;
    ctx.traceScene.resources.addAsset(relSrc, blob);
    return relSrc;
}

/**
 * Generate all images queued by build_spatial's image() helper, in parallel,
 * and commit each src to its waiting Image object. Call after the agent loop
 * (or between turns) — never inside it.
 */
export async function resolvePendingImages(ctx: ToolContext): Promise<string> {
    const pending = ctx.pendingImages?.splice(0, ctx.pendingImages.length) ?? [];
    if (pending.length === 0) return 'No pending images.';
    console.log(`[AI] resolvePendingImages: generating ${pending.length} image(s) in parallel`);
    const results = await Promise.all(pending.map(async ({ object, prompt }) => {
        try {
            const gen = await generateImageBytes(ctx, prompt);
            if ('error' in gen) return `✗ "${object.name}": ${gen.error}`;
            const relSrc = await persistGeneratedImage(ctx, gen.bytes, gen.blob, gen.extension);
            const data = object.getSceneObjectData();
            data.src = relSrc;
            await object.setData(data);
            return `✓ "${object.name}" ← ${relSrc}`;
        } catch (err) {
            return `✗ "${object.name}": ${err instanceof Error ? err.message : String(err)}`;
        }
    }));
    ctx.onSceneChanged?.();
    return `Resolved ${results.filter(r => r.startsWith('✓')).length}/${pending.length} pending images:\n${results.join('\n')}`;
}

async function executeGenerateImage(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const prompt = input.prompt as string;
    const name = input.name as string | undefined;
    const transformArgs = readTransformArgs(input);
    if ('error' in transformArgs) return transformArgs.error;
    const { position, rotation, scale } = transformArgs;

    console.log(`[AI] generate_image: requesting image for "${prompt?.slice(0, 100)}"`);
    const gen = await generateImageBytes(ctx, prompt);
    if ('error' in gen) {
        console.error('[AI] generate_image error:', gen.error);
        return `Error generating image: ${gen.error}`;
    }
    const { bytes, blob, extension } = gen;
    const sanitized = prompt.slice(0, 40).replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
    const fileName = `generated_${sanitized}.${extension}`;
    const objectName = name ?? (prompt.slice(0, 30).replace(/[^a-zA-Z0-9 ]/g, '').trim() || 'GeneratedImage');

    // Import blob into scene
    const viewer = ctx.viewer;
    if (!viewer?.import) {
        // Headless (bench/MCP): no <trace-viewer> element to import through.
        // Persist the image under public/generated/ (servable by the dev and
        // render harness), register the bytes in ResourceManager for the live
        // session, and build an Image-behavior object directly — the same
        // pattern the bench preloader uses.
        try {
            const relSrc = await persistGeneratedImage(ctx, bytes, blob, extension);
            const { traceScene } = ctx;
            const obj = await traceScene.createSceneObject(objectName, topLevelParent(traceScene) ?? undefined, relSrc);
            await obj.addBehavior('Image');
            groundAlign(obj.transform);
            applyTransform(obj.transform, position, rotation, scale);
            ctx.traceScene.select(obj);
            ctx.onSceneChanged?.();
            console.log(`[AI] generate_image (headless): created "${obj.name}" -> ${relSrc} (${blob.size} bytes)`);
            return `Generated image "${obj.name}" from prompt and added to scene. The image quad already stands UPRIGHT facing the viewer (+Z) — do NOT rotate it to "stand it up" (unlike the flat "plane" primitive). Scale it via X (width) and Y (height); its aspect is set automatically. Reparent or position it like any object.`;
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            return `Error adding generated image to scene (headless): ${msg}`;
        }
    }

    try {
        // A CMS scene owns a folder; put the generated bytes in it now so the
        // object is authored with a folder-relative `src` (spec §2.6).
        const folderImport = await importBlobIntoSceneFolder(blob, fileName, ctx.traceScene);
        const { sceneObject } = await viewer.import(
            folderImport.file, folderImport.file.name, objectName,
        );
        // `files[k].name` carries the generated file's own name, not the
        // hash the folder stored it under (spec §2.1).
        registerFolderFileName(ctx.traceScene, folderImport);

        groundAlign(sceneObject.transform);
        applyTransform(sceneObject.transform, position, rotation, scale);

        // Dispatch event for undo recording
        await dispatchCatalogImport(sceneObject);

        ctx.traceScene.select(sceneObject);
        ctx.onSceneChanged?.();

        console.log(`[AI] generate_image: created "${sceneObject.name}" (${blob.type}, ${blob.size} bytes)`);
        return `Generated image "${sceneObject.name}" from prompt and added to scene. The image quad already stands UPRIGHT facing the viewer (+Z) — do NOT rotate it to "stand it up" (unlike the flat "plane" primitive). Scale via X (width) and Y (height).`;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        return `Error adding generated image to scene: ${msg}`;
    }
}

// ── analyze_image ──────────────────────────────────────────

/** The picture a screenshot's data URL holds, typed by it. */
function dataUrlBlob(dataUrl: string): Blob {
    const match = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(dataUrl);
    if (!match?.[2]) throw new Error('the screenshot is not a base64 data URL.');
    const binary = atob(match[3] ?? '');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: match[1] || 'image/png' });
}

/** The picture to analyze, typed (`image/png` when its source names no type), and what it shows. */
async function resolveImageSource(
    source: string,
    ctx: ToolContext,
): Promise<{ blob: Blob; label: string }> {
    // Screenshot (default).
    if (source === 'screenshot') {
        const scene = ctx.traceScene.getBabylonScene();
        const camera = scene.activeCamera;
        if (!camera) throw new Error('No active camera for screenshot.');
        const engine = scene.getEngine();
        // Never await a frame that can't come: an engine with no render loop
        // (headless NullEngine) parks CreateScreenshotAsync forever.
        if (engine.constructor.name === 'NullEngine' || engine.activeRenderLoops.length === 0) {
            throw new Error('screenshots are unavailable in this headless environment (no render loop) — analyze a named Image object instead.');
        }
        const dataUrl = await Promise.race([
            ScreenshotTools.CreateScreenshotAsync(engine, camera, { width: 1280, height: 720 }, 'image/png'),
            new Promise<string>((_, reject) => setTimeout(
                () => reject(new Error('screenshot timed out — no frame was rendered within 15s.')), 15_000)),
        ]);
        return { blob: dataUrlBlob(dataUrl), label: 'viewport screenshot' };
    }

    // Object reference — "selected", name, or "object:<name>".
    const ref = source.startsWith('object:') ? source.slice('object:'.length) : source;
    const obj = resolveObject(ref, ctx);
    if (!obj) throw new Error(`Object "${ref}" not found.`);

    const hasVideo = !!obj.getBehavior(Video);
    const hasImage = !!obj.getBehavior(Image);
    if (!hasVideo && (!hasImage || !obj.src)) {
        const candidates = ctx.traceScene.getSceneObjects()
            .filter(o => o.getBehavior(Video) || o.getBehavior(Image))
            .map(o => `"${o.name}"`);
        const hint = candidates.length > 0
            ? ` Image or Video objects in the scene: ${candidates.join(', ')}. Pass one of those as source instead.`
            : '';
        throw new Error(
            `Object "${obj.name}" has no Image or Video behavior.${hint}`,
        );
    }

    const blob = await getImageBlob(obj, ctx.traceScene);
    const label = hasVideo ? `current frame of video "${obj.name}"` : `image on "${obj.name}"`;
    return { blob: blob.type ? blob : new Blob([blob], { type: 'image/png' }), label };
}

async function executeAnalyzeImage(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const prompt = (input.prompt as string | undefined)?.trim();
    const source = (input.source as string | undefined) ?? 'screenshot';

    if (!prompt) {
        return 'Error: A prompt describing what to analyze or extract is required.';
    }

    let image: { blob: Blob; label: string };
    try {
        image = await resolveImageSource(source, ctx);
    } catch (err) {
        return `Error: ${err instanceof Error ? err.message : String(err)}`;
    }

    try {
        console.log(`[AI] analyze_image: analyzing ${image.label} — "${prompt.slice(0, 80)}"`);
        const result = await callCapability<{ text: string }>('vision/analyze', { prompt }, ctx.signal, { image: [image.blob] });
        const text = result.text?.trim();
        if (!text) return 'Error: The analysis model returned no text. Try rephrasing the prompt.';
        return text;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error('[AI] analyze_image gateway error:', msg);
        return `Error analyzing image: ${msg}`;
    }
}

// ── build_spatial ──────────────────────────────────────────

async function executeBuildSpatial(
    input: Record<string, unknown>,
    ctx: ToolContext,
): Promise<string> {
    const code = input.code as string | undefined;
    const description = input.description as string;
    const appendTo = typeof input.parent === 'string' && input.parent.trim() ? input.parent.trim() : undefined;

    if (!code) {
        return 'Error: No code was generated. build_spatial can only CREATE new objects — it cannot modify existing ones. To resize, move, recolor, or delete existing objects use set_transform, set_color, delete_object, etc. To duplicate existing objects use duplicate_object.';
    }

    console.log(`[AI] build_spatial: executing code for "${description}" (${code.length} chars)`);

    // Build read-only snapshot of existing scene for getObject()/listObjects() access
    const existingScene: SpatialSceneSnapshot[] = [];
    for (const obj of ctx.traceScene.getSceneObjects()) {
        // The engine mount point is not an object; the document root is one.
        if (isEngineRoot(obj)) continue;
        obj.captureTransform();
        const pos = obj.transform.position;
        const rot = obj.transform.rotation;
        const scl = obj.transform.scaling;
        const model = obj.getBehavior(Model);
        const dims = model?.getDimensions();
        const r = (v: number) => Math.round(v * 1000) / 1000;
        existingScene.push({
            name: obj.name,
            path: obj.getPath(),
            position: [r(pos.x), r(pos.y), r(pos.z)],
            rotation: [r(rot.x), r(rot.y), r(rot.z)],
            scale: [r(scl.x), r(scl.y), r(scl.z)],
            active: obj.active,
            src: obj.src,
            dimensions: dims ? [r(dims.x), r(dims.y), r(dims.z)] : undefined,
            bounds: boundsInParent(obj),
            behaviors: obj.getBehaviors().map(b => b.name),
        });
    }

    // Phase 1: Execute in sandbox. The budget is a SCENE budget, so the
    // sandbox is told what is already there (R4-C).
    const result = executeSpatialCode(code, undefined, existingScene);

    if (result.errors.length > 0) {
        console.warn(`[AI] build_spatial sandbox errors:`, result.errors);
        const joined = result.errors.join(' ');
        // Detect attempts to reference existing scene objects by name via handle APIs
        const unknownHandle = result.errors.some(e => e.includes('unknown object handle') || e.includes('unknown child handle') || e.includes('unknown parent handle'));
        // Detect reaching for APIs that aren't in the sandbox (getTransform, scene.find, etc.)
        const reachingOutside = /\b(getTransform|get_transform|scene\.|document\.|window\.|require|fetch)\b/.test(joined);
        let hint = '\n\nFix the code and try again.';
        if (unknownHandle) {
            hint = '\n\nTo MODIFY existing objects, use direct tools (set_transform, set_color, duplicate_object). Inside build_spatial, use `clone(name)` to copy an existing object, or `getObject(name)` to read its position/scale/bounds (read-only).';
        } else if (reachingOutside) {
            hint = '\n\nThat API is not in the spatial sandbox. To READ existing scene objects, use `getObject(name)` — returns {position, rotation, scale, dimensions, bounds, behaviors}. To MODIFY, use direct tools outside build_spatial.';
        }
        return `Build error:\n${result.errors.join('\n')}${hint}`;
    }

    console.log(`[AI] build_spatial: sandbox produced ${result.objects.length} objects`);

    if (result.objects.length === 0) {
        return 'Error: Build produced no objects; no scene changes were made. Inspection-only code is not a build. Use get_scene_tree/get_transform for inspection, then delegate a bounded construction task. The code must call cube(), sphere(), cylinder(), plane(), group(), clone(), or asset() to create objects.';
    }

    // Phase 2: Materialize into scene
    try {
        if (result.stats.instanceCalls > 0) {
            const acc = ctx.spatialInstances ??= { calls: 0, objects: 0 };
            acc.calls += result.stats.instanceCalls;
            acc.objects += result.stats.instanceObjects;
        }
        const msg = await materializeSpatialBuild(result.objects, description, ctx, {
            interactions: result.interactions,
            environment: result.environment,
            appendTo,
            stats: result.stats,
            // Progress keeps the turn watchdog alive through a long build.
            onProgress: (created, total) => ctx.onToolProgress?.(`materializing ${created}/${total} objects`),
        });
        console.log(`[AI] build_spatial result:`, msg);
        // Non-fatal notes ride back with the success message — the build is
        // already in the scene, so these are "fix it next turn", not errors.
        if (result.warnings.length > 0) {
            return `${msg}\n\n${result.warnings.join('\n')}`;
        }
        return msg;
    } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.error(`[AI] build_spatial materialization error:`, msg);
        return `Materialization error: ${msg}`;
    }
}

// ── Exports ────────────────────────────────────────────────

export const generationTools: ToolDefinition[] = [
    {
        tool: {
            name: 'generate_image',
            input_schema: {
                type: 'object' as const,
                properties: {
                    prompt: { type: 'string' },
                    name: { type: 'string' },
                    position: {
                        type: 'array',
                        items: { type: 'number' },
                        minItems: 3,
                        maxItems: 3,
                    },
                    rotation: {
                        type: 'array',
                        items: { type: 'number' },
                        minItems: 3,
                        maxItems: 3,
                    },
                    scale: {
                        type: 'array',
                        items: { type: 'number' },
                        minItems: 3,
                        maxItems: 3,
                    },
                },
                required: ['prompt'],
            },
        },
        execute: executeGenerateImage,
        modelLabel: imageModelLabel('image'),
    },
    {
        tool: {
            name: 'analyze_image',
            input_schema: {
                type: 'object' as const,
                properties: {
                    prompt: { type: 'string' },
                    source: { type: 'string' },
                },
                required: ['prompt'],
            },
        },
        execute: executeAnalyzeImage,
        modelLabel: imageModelLabel('vision'),
    },
    {
        tool: {
            name: 'build_spatial',
            input_schema: {
                type: 'object' as const,
                properties: {
                    context_objects: contextObjectsSchema,
                    code: { type: 'string' },
                    description: { type: 'string' },
                    parent: { type: 'string' },
                },
                required: ['code', 'description'],
            },
        },
        execute: editModeOnly(executeBuildSpatial),
        specialist: { context: async (input, ctx) => ({ context: await buildRestoredGeometryContext(input, ctx) }) },
    },
];
